import React, { Component } from 'react';
import { WidgetHelper } from '../WidgetHelper';
import {
    IEventLight,
    AgendaFilterDate,
    IHashtag,
    ICategoryEvent,
    IEventParameters,
} from '../../Models';
import { EventService } from '../../Services';
import { EventItem, EventSearch, FloatLabel } from '../../Components';
import Select, { SingleValue } from 'react-select';

interface IEventListProps {
    title: string;
    broadcastChannelExternalIds: string;
    hideFilters: string;
    dictNoEventLabel: string;
    eventLink: string;
    dictNoResultTitle: string;
    dictNoResultDescription: string;

    dictAll: string;
    dictThisWeek: string;
    dictNextWeek: string;
    dictAfter: string;
    dictThemes: string;
    dictAllThisWeek: string;
    dictAllNextWeek: string;
    dictAllAfter: string;
    dictDomain: string;
    dictEventType: string;
    dictSearch: string;
    all: string;
}

interface IEventListWidgetState {
    eventsThisWeek: IEventLight[];
    eventsNextWeek: IEventLight[];
    eventsAfter: IEventLight[];
    hasMoreEvent: boolean;
    dateFilter: AgendaFilterDate;

    selectCategory: ICategoryEvent | null;
    selectHashtag: IHashtag | null;

    hashtags: IHashtag[];
    categories: ICategoryEvent[];
    eventParameters: IEventParameters;
    hideFilters: boolean;
    isLoading: boolean;
}

export class EventListWidget extends Component<
    IEventListProps,
    IEventListWidgetState
> {
    constructor(props: any) {
        super(props);
        this.state = {
            eventsThisWeek: [],
            eventsNextWeek: [],
            eventsAfter: [],
            hasMoreEvent: false,
            dateFilter: AgendaFilterDate.All,
            selectCategory: null,
            selectHashtag: null,
            hashtags: [],
            categories: [],
            eventParameters: {
                broadcastChannelExternalIds: this.props
                    .broadcastChannelExternalIds
                    ? this.props.broadcastChannelExternalIds
                          .split(',')
                          .map((id) => parseInt(id, 10))
                    : [],
                categories: [],
                hashtags: [],
                keyword: '',
            },
            hideFilters: this.props.hideFilters === 'True',
            isLoading: true,
        };

        void this.getAllEvent();
        void this.getHashtag();
        void this.getCategories();
    }

    async getHashtag() {
        try {
            const hashtags = await EventService.GetHashtag();
            const all: IHashtag = { id: 0, value: this.props.all };
            hashtags.splice(0, 0, all);
            this.setState({ hashtags });
        } catch (e) {
            if (!(e instanceof DOMException) || e.name !== 'AbortError') {
                throw e;
            }
        }
    }

    async getCategories() {
        try {
            const categories = await EventService.GetCategories();
            const all: ICategoryEvent = { id: 0, name: this.props.all };
            categories.splice(0, 0, all);
            this.setState({ categories });
        } catch (e) {
            if (!(e instanceof DOMException) || e.name !== 'AbortError') {
                throw e;
            }
        }
    }

    async getAllEvent() {
        this.setState({ isLoading: true });
        try {
            const events = await EventService.getAll(
                this.state.eventParameters
            );

            // remap events to have correct Date object inside
            const eventsWithDate = events.map((event) => ({
                category: event.category,
                dateEnd: event.dateEnd ? new Date(event.dateEnd) : undefined,
                dateStart: new Date(event.dateStart),
                dateEndString: event.dateEndString,
                dateStartString: event.dateStartString,
                description: event.description,
                reference: event.reference,
                title: event.title,
            }));

            // format current Date
            const currentDateTime: Date = new Date();
            const currentDate = new Date(
                currentDateTime.getFullYear(),
                currentDateTime.getMonth(),
                currentDateTime.getDate()
            );

            // set the range values
            const startOfThisWeekDate = new Date(currentDate);
            startOfThisWeekDate.setDate(
                startOfThisWeekDate.getDate() - startOfThisWeekDate.getDay() + 1
            ); // +1 because belgium week starts on monday
            const endOfThisWeek = new Date(currentDate);
            endOfThisWeek.setDate(
                endOfThisWeek.getDate() - endOfThisWeek.getDay() + 8
            ); // +8 to reach monday of the next week
            const endOfNextWeekDate = new Date(currentDate);
            endOfNextWeekDate.setDate(
                endOfNextWeekDate.getDate() - endOfNextWeekDate.getDay() + 15
            ); // +15 for the end of the 2 next weeks

            // set result
            const eventsThisWeek = eventsWithDate.filter(
                (e) => e.dateStart && e.dateStart < endOfThisWeek
            );
            const eventsNextWeek = eventsWithDate.filter(
                (e) =>
                    e.dateStart &&
                    e.dateStart >= endOfThisWeek &&
                    e.dateStart < endOfNextWeekDate
            );
            const eventsAfter = eventsWithDate.filter(
                (e) => e.dateStart && e.dateStart >= endOfNextWeekDate
            );

            this.setState({
                eventsThisWeek,
                eventsNextWeek,
                eventsAfter,
                isLoading: false,
            });
        } catch (e) {
            if (!(e instanceof DOMException) || e.name !== 'AbortError') {
                throw e;
            }
        }
    }

    handleFormSubmit = (newKeyword: string) => {
        this.setState(
            (prevState) => ({
                eventParameters: {
                    ...prevState.eventParameters,
                    keyword: newKeyword,
                },
            }),
            () => {
                void this.getAllEvent();
            }
        );
    };

    handleLoadMore = () => {
        void this.getAllEvent();
    };

    handleThisWeek = () => {
        this.setState({ dateFilter: AgendaFilterDate.ThisWeek });
    };

    handleNextWeek = () => {
        this.setState({ dateFilter: AgendaFilterDate.NextWeek });
    };

    handleAfter = () => {
        this.setState({ dateFilter: AgendaFilterDate.After });
    };

    handleAll = () => {
        this.setState({ dateFilter: AgendaFilterDate.All });
    };

    handleSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
        this.setState({
            dateFilter: Number(event.target.value) as AgendaFilterDate,
        });
    };

    floatLabelActive(selectedValue: IHashtag | ICategoryEvent | null): boolean {
        return selectedValue !== null;
    }

    onSelectCategory = (selectedItem: ICategoryEvent | null) => {
        if (!selectedItem) return;

        this.setState(
            (prevState) => ({
                selectCategory: selectedItem,
                eventParameters: {
                    ...prevState.eventParameters,
                    categories: selectedItem.id === 0 ? [] : [selectedItem.id],
                },
            }),
            () => void this.getAllEvent()
        );
    };

    onSelectHashtag = (selectedItem: SingleValue<IHashtag>) => {
        if (!selectedItem) return;

        this.setState(
            (prevState) => ({
                selectHashtag: selectedItem,
                eventParameters: {
                    ...prevState.eventParameters,
                    hashtags: selectedItem.id === 0 ? [] : [selectedItem.id],
                },
            }),
            () => void this.getAllEvent()
        );
    };

    render() {
        if (
            this.state.eventsThisWeek.length > 0 ||
            this.state.eventsNextWeek.length > 0 ||
            this.state.eventsAfter.length > 0
        ) {
            return (
                <>
                    <div className="banner banner--colored banner--colored__event">
                        <div className="container">
                            <div className="row">
                                <div className="col-12 col-lg-8 offset-lg-2">
                                    <h1>{this.props.title}</h1>
                                    {!this.state.hideFilters && (
                                        <div className="form-group">
                                            <div className="d-inline-block col-6 col-lg-4 offset-lg-2 ">
                                                <FloatLabel
                                                    active={this.floatLabelActive(
                                                        this.state.selectHashtag
                                                    )}
                                                    label={
                                                        this.props.dictDomain
                                                    }
                                                    labelId={
                                                        this.props.dictDomain
                                                    }
                                                >
                                                    <Select
                                                        aria-labelledby={
                                                            this.props
                                                                .dictDomain
                                                        }
                                                        placeholder={
                                                            this.props
                                                                .dictDomain
                                                        }
                                                        value={
                                                            this.state
                                                                .selectHashtag
                                                        }
                                                        options={
                                                            this.state.hashtags
                                                        }
                                                        onChange={
                                                            this.onSelectHashtag
                                                        }
                                                        getOptionLabel={(
                                                            option: IHashtag
                                                        ) => option.value}
                                                        getOptionValue={(
                                                            option: IHashtag
                                                        ) => String(option.id)}
                                                        className="filter-select-container"
                                                        classNamePrefix="filter-select"
                                                        isSearchable={false}
                                                    />
                                                </FloatLabel>
                                            </div>
                                            <div className="d-inline-block col-6 col-lg-4 offset-lg-2">
                                                <FloatLabel
                                                    active={this.floatLabelActive(
                                                        this.state
                                                            .selectCategory
                                                    )}
                                                    label={
                                                        this.props.dictEventType
                                                    }
                                                    labelId={
                                                        this.props.dictEventType
                                                    }
                                                >
                                                    <Select
                                                        aria-labelledby={
                                                            this.props
                                                                .dictEventType
                                                        }
                                                        placeholder={
                                                            this.props
                                                                .dictEventType
                                                        }
                                                        value={
                                                            this.state
                                                                .selectCategory
                                                        }
                                                        options={
                                                            this.state
                                                                .categories
                                                        }
                                                        onChange={
                                                            this
                                                                .onSelectCategory
                                                        }
                                                        getOptionLabel={(
                                                            option: ICategoryEvent
                                                        ) => option.name}
                                                        getOptionValue={(
                                                            option: ICategoryEvent
                                                        ) => String(option.id)}
                                                        className="filter-select-container"
                                                        classNamePrefix="filter-select"
                                                        isSearchable={false}
                                                    />
                                                </FloatLabel>
                                            </div>
                                        </div>
                                    )}
                                    <div className="tab__filters d-none d-lg-flex justify-content-between">
                                        <a
                                            href="#"
                                            className={
                                                this.state.dateFilter ==
                                                AgendaFilterDate.All
                                                    ? 'active'
                                                    : 'all'
                                            }
                                            onClick={this.handleAll}
                                        >
                                            {this.props.dictAll}
                                        </a>
                                        <a
                                            href="#"
                                            className={
                                                this.state.dateFilter ==
                                                AgendaFilterDate.ThisWeek
                                                    ? 'active'
                                                    : 'thisWeek'
                                            }
                                            onClick={this.handleThisWeek}
                                        >
                                            {this.props.dictThisWeek}
                                        </a>
                                        <a
                                            href="#"
                                            className={
                                                this.state.dateFilter ==
                                                AgendaFilterDate.NextWeek
                                                    ? 'active'
                                                    : 'nextWeek'
                                            }
                                            onClick={this.handleNextWeek}
                                        >
                                            {this.props.dictNextWeek}
                                        </a>
                                        <a
                                            href="#"
                                            className={
                                                this.state.dateFilter ==
                                                AgendaFilterDate.After
                                                    ? 'active'
                                                    : 'after'
                                            }
                                            onClick={this.handleAfter}
                                        >
                                            {this.props.dictAfter}
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="container">
                        <div className="row mt-5 no-gutters">
                            <div className="col-12 col-lg-8 offset-lg-2">
                                <EventSearch
                                    onFormSubmit={this.handleFormSubmit}
                                    dictSearch={this.props.dictSearch}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="container">
                        <div className="row mb-5 no-gutters">
                            <div className="col-12 col-lg-8 offset-lg-2">
                                <div className="results-topbar d-flex d-lg-none justify-content-end">
                                    <form className="d-inline-block">
                                        <div className="form-group d-inline-block">
                                            <label className="sr-only sr-only-focusable">
                                                {this.props.dictThemes}
                                            </label>
                                            <select
                                                value={this.state.dateFilter}
                                                onChange={this.handleSelect}
                                            >
                                                <option
                                                    value={AgendaFilterDate.All}
                                                >
                                                    {this.props.dictAll}
                                                </option>
                                                <option
                                                    value={
                                                        AgendaFilterDate.ThisWeek
                                                    }
                                                >
                                                    {this.props.dictThisWeek}
                                                </option>
                                                <option
                                                    value={
                                                        AgendaFilterDate.NextWeek
                                                    }
                                                >
                                                    {this.props.dictNextWeek}
                                                </option>
                                                <option
                                                    value={
                                                        AgendaFilterDate.After
                                                    }
                                                >
                                                    {this.props.dictAfter}
                                                </option>
                                            </select>
                                        </div>
                                    </form>
                                </div>
                                <div className="tiles tiles--bordered">
                                    {this.state.isLoading && (
                                        <div className="lds-ripple">
                                            <div></div>
                                            <div></div>
                                        </div>
                                    )}
                                    {!this.state.isLoading && (
                                        <>
                                            {this.state.eventsThisWeek.length >
                                                0 &&
                                                (this.state.dateFilter ==
                                                    AgendaFilterDate.ThisWeek ||
                                                    this.state.dateFilter ==
                                                        AgendaFilterDate.All) && (
                                                    <div className="tiles__group">
                                                        <h2>
                                                            {
                                                                this.props
                                                                    .dictThisWeek
                                                            }
                                                        </h2>
                                                        {this.state
                                                            .eventsThisWeek &&
                                                            this.state.eventsThisWeek.map(
                                                                (event) => (
                                                                    <EventItem
                                                                        {...event}
                                                                        eventLink={
                                                                            this
                                                                                .props
                                                                                .eventLink
                                                                        }
                                                                        key={
                                                                            event.reference
                                                                        }
                                                                    />
                                                                )
                                                            )}
                                                        <div className="align-right">
                                                            <a
                                                                className="more"
                                                                href="#"
                                                                onClick={
                                                                    this
                                                                        .handleThisWeek
                                                                }
                                                            >
                                                                {
                                                                    this.props
                                                                        .dictAllThisWeek
                                                                }
                                                            </a>
                                                        </div>
                                                    </div>
                                                )}
                                            {this.state.eventsThisWeek.length ==
                                                0 &&
                                                this.state.dateFilter ==
                                                    AgendaFilterDate.ThisWeek && (
                                                    <div className="empty-state">
                                                        <i className="icon-no-results"></i>
                                                        <p>
                                                            <b>
                                                                {
                                                                    this.props
                                                                        .dictNoResultTitle
                                                                }
                                                            </b>
                                                        </p>
                                                        <p>
                                                            {
                                                                this.props
                                                                    .dictNoResultDescription
                                                            }
                                                        </p>
                                                    </div>
                                                )}

                                            {this.state.eventsNextWeek.length >
                                                0 &&
                                                (this.state.dateFilter ==
                                                    AgendaFilterDate.NextWeek ||
                                                    this.state.dateFilter ==
                                                        AgendaFilterDate.All) && (
                                                    <div className="tiles__group">
                                                        <h2>
                                                            {
                                                                this.props
                                                                    .dictNextWeek
                                                            }
                                                        </h2>
                                                        {this.state
                                                            .eventsNextWeek &&
                                                            this.state.eventsNextWeek.map(
                                                                (event) => (
                                                                    <EventItem
                                                                        {...event}
                                                                        eventLink={
                                                                            this
                                                                                .props
                                                                                .eventLink
                                                                        }
                                                                        key={
                                                                            event.reference
                                                                        }
                                                                    />
                                                                )
                                                            )}
                                                        <div className="align-right">
                                                            <a
                                                                className="more"
                                                                href="#"
                                                                onClick={
                                                                    this
                                                                        .handleNextWeek
                                                                }
                                                            >
                                                                {
                                                                    this.props
                                                                        .dictAllNextWeek
                                                                }
                                                            </a>
                                                        </div>
                                                    </div>
                                                )}
                                            {this.state.eventsNextWeek.length ==
                                                0 &&
                                                this.state.dateFilter ==
                                                    AgendaFilterDate.NextWeek && (
                                                    <div className="empty-state">
                                                        <i className="icon-no-results"></i>
                                                        <p>
                                                            <b>
                                                                {
                                                                    this.props
                                                                        .dictNoResultTitle
                                                                }
                                                            </b>
                                                        </p>
                                                        <p>
                                                            {
                                                                this.props
                                                                    .dictNoResultDescription
                                                            }
                                                        </p>
                                                    </div>
                                                )}

                                            {this.state.eventsAfter.length >
                                                0 &&
                                                (this.state.dateFilter ==
                                                    AgendaFilterDate.After ||
                                                    this.state.dateFilter ==
                                                        AgendaFilterDate.All) && (
                                                    <div className="tiles__group">
                                                        <h2>
                                                            {
                                                                this.props
                                                                    .dictAfter
                                                            }
                                                        </h2>
                                                        {this.state
                                                            .eventsAfter &&
                                                            this.state.eventsAfter.map(
                                                                (event) => (
                                                                    <EventItem
                                                                        {...event}
                                                                        eventLink={
                                                                            this
                                                                                .props
                                                                                .eventLink
                                                                        }
                                                                        key={
                                                                            event.reference
                                                                        }
                                                                    />
                                                                )
                                                            )}
                                                        <div className="align-right">
                                                            <a
                                                                className="more"
                                                                href="#"
                                                                onClick={
                                                                    this
                                                                        .handleAfter
                                                                }
                                                            >
                                                                {
                                                                    this.props
                                                                        .dictAllAfter
                                                                }
                                                            </a>
                                                        </div>
                                                    </div>
                                                )}
                                            {this.state.eventsAfter.length ==
                                                0 &&
                                                this.state.dateFilter ==
                                                    AgendaFilterDate.After && (
                                                    <div className="empty-state">
                                                        <i className="icon-no-results"></i>
                                                        <p>
                                                            <b>
                                                                {
                                                                    this.props
                                                                        .dictNoResultTitle
                                                                }
                                                            </b>
                                                        </p>
                                                        <p>
                                                            {
                                                                this.props
                                                                    .dictNoResultDescription
                                                            }
                                                        </p>
                                                    </div>
                                                )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            );
        }

        return (
            <>
                <div className="banner banner--colored banner--colored__event">
                    <div className="container">
                        <div className="row">
                            <div className="col-12 col-lg-8 offset-lg-2">
                                <h1>{this.props.title}</h1>
                                {!this.state.hideFilters && (
                                    <div className="form-group">
                                        <div className="d-inline-block col-6 col-lg-4 offset-lg-2 ">
                                            <FloatLabel
                                                active={this.floatLabelActive(
                                                    this.state.selectHashtag
                                                )}
                                                label={this.props.dictDomain}
                                                labelId={this.props.dictDomain}
                                            >
                                                <Select
                                                    aria-labelledby={
                                                        this.props.dictDomain
                                                    }
                                                    placeholder={
                                                        this.props.dictDomain
                                                    }
                                                    value={
                                                        this.state.selectHashtag
                                                    }
                                                    options={
                                                        this.state.hashtags
                                                    }
                                                    onChange={
                                                        this.onSelectHashtag
                                                    }
                                                    getOptionLabel={(
                                                        option: IHashtag
                                                    ) => option.value}
                                                    getOptionValue={(
                                                        option: IHashtag
                                                    ) => String(option.id)}
                                                    className="filter-select-container"
                                                    classNamePrefix="filter-select"
                                                    isSearchable={false}
                                                />
                                            </FloatLabel>
                                        </div>
                                        <div className="d-inline-block col-6 col-lg-4 offset-lg-2">
                                            <FloatLabel
                                                active={this.floatLabelActive(
                                                    this.state.selectCategory
                                                )}
                                                label={this.props.dictEventType}
                                                labelId={
                                                    this.props.dictEventType
                                                }
                                            >
                                                <Select
                                                    aria-labelledby={
                                                        this.props.dictEventType
                                                    }
                                                    placeholder={
                                                        this.props.dictEventType
                                                    }
                                                    value={
                                                        this.state
                                                            .selectCategory
                                                    }
                                                    options={
                                                        this.state.categories
                                                    }
                                                    onChange={
                                                        this.onSelectCategory
                                                    }
                                                    getOptionLabel={(
                                                        option: ICategoryEvent
                                                    ) => option.name}
                                                    getOptionValue={(
                                                        option: ICategoryEvent
                                                    ) => String(option.id)}
                                                    className="filter-select-container"
                                                    classNamePrefix="filter-select"
                                                    isSearchable={false}
                                                />
                                            </FloatLabel>
                                        </div>
                                    </div>
                                )}
                                <div className="tab__filters d-none d-lg-flex justify-content-between">
                                    <a
                                        href="#"
                                        className={
                                            this.state.dateFilter ==
                                            AgendaFilterDate.All
                                                ? 'active'
                                                : 'all'
                                        }
                                        onClick={this.handleAll}
                                    >
                                        {this.props.dictAll}
                                    </a>
                                    <a
                                        href="#"
                                        className={
                                            this.state.dateFilter ==
                                            AgendaFilterDate.ThisWeek
                                                ? 'active'
                                                : 'thisWeek'
                                        }
                                        onClick={this.handleThisWeek}
                                    >
                                        {this.props.dictThisWeek}
                                    </a>
                                    <a
                                        href="#"
                                        className={
                                            this.state.dateFilter ==
                                            AgendaFilterDate.NextWeek
                                                ? 'active'
                                                : 'nextWeek'
                                        }
                                        onClick={this.handleNextWeek}
                                    >
                                        {this.props.dictNextWeek}
                                    </a>
                                    <a
                                        href="#"
                                        className={
                                            this.state.dateFilter ==
                                            AgendaFilterDate.After
                                                ? 'active'
                                                : 'after'
                                        }
                                        onClick={this.handleAfter}
                                    >
                                        {this.props.dictAfter}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="container">
                    <div className="row mt-5 no-gutters">
                        <div className="col-12 col-lg-8 offset-lg-2">
                            <EventSearch
                                onFormSubmit={this.handleFormSubmit}
                                dictSearch={this.props.dictSearch}
                            />
                        </div>
                    </div>
                </div>
                <div className="container">
                    <div className="row intro no-gutters">
                        <div className="col-12 col-lg-8 offset-lg-2">
                            <div className="results-topbar d-flex d-lg-none justify-content-end">
                                <form className="d-inline-block">
                                    <div className="form-group d-inline-block">
                                        <label className="sr-only sr-only-focusable">
                                            {this.props.dictThemes}
                                        </label>
                                        <select
                                            value={this.state.dateFilter}
                                            onChange={this.handleSelect}
                                        >
                                            <option
                                                value={AgendaFilterDate.All}
                                            >
                                                {this.props.dictAll}
                                            </option>
                                            <option
                                                value={
                                                    AgendaFilterDate.ThisWeek
                                                }
                                            >
                                                {this.props.dictThisWeek}
                                            </option>
                                            <option
                                                value={
                                                    AgendaFilterDate.NextWeek
                                                }
                                            >
                                                {this.props.dictNextWeek}
                                            </option>
                                            <option
                                                value={AgendaFilterDate.After}
                                            >
                                                {this.props.dictAfter}
                                            </option>
                                        </select>
                                    </div>
                                </form>
                            </div>
                            {this.state.isLoading && (
                                <div className="lds-ripple">
                                    <div></div>
                                    <div></div>
                                </div>
                            )}
                            {!this.state.isLoading && (
                                <div className="empty-state">
                                    <i className="icon-no-results"></i>
                                    <p>
                                        <b>{this.props.dictNoResultTitle}</b>
                                    </p>
                                    <p>{this.props.dictNoResultDescription}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </>
        );
    }
}

WidgetHelper.register(EventListWidget, 'event-list-widget');
